import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/styles';
import { Box, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { Search, Settings } from '@material-ui/icons';
import SpinnerComponent from '../../../components/SpinnerComponent';
import NewRecordButton from '../components/NewRecordButton';
import TablePageToolbar from '../components/TablePageToolbar';
import { getBrandCount, getBrands } from '../../../utils/api/brand_api';
import BasicTable from '../../../components/BasicTable';
import { reportApiError } from '../../../utils/reportApiError';

/** What both the typed filter and the applied one start out as. */
const NO_FILTER = { brandId: '', brandName: '' };

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(5),
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  column: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorBox: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
  },
}));

const BrandTable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: 10,
    loadedPage: 0,
    totalPages: 1,
  });
  // `filter` is what is typed in the column inputs; `appliedFilter` is what the
  // list on screen is actually filtered by. They part company until the user
  // presses Search, which is what makes the list a function of page and applied
  // filter together - and so what keeps the filter when the page turns.
  const [filter, setFilter] = useState(NO_FILTER);
  const [appliedFilter, setAppliedFilter] = useState(NO_FILTER);

  // The only place brands are read: page, page size and applied filter all
  // arrive here, so no two reads can race to answer for the same list.
  useEffect(() => {
    let isMounted = true;
    const { brandId, brandName } = appliedFilter;

    getBrandCount(brandId, brandName)
      .then((countResponse) => {
        return getBrands(
          paginationConfig.loadedPage,
          paginationConfig.pageSize,
          'id',
          brandId,
          brandName,
        ).then((response) => {
          if (isMounted) {
            setBrands(response);
            setPaginationConfig((prev) => ({
              ...prev,
              totalPages:
                countResponse !== 0
                  ? Math.ceil(countResponse / prev.pageSize)
                  : 1,
            }));
          }
        });
      })
      .catch(reportApiError)
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [paginationConfig.loadedPage, paginationConfig.pageSize, appliedFilter]);

  const handleClick = (row) => {
    history.push({
      pathname: `/amministrazione/edit_template/${row.id}`,
      state: {
        id: row.id,
        brandName: row.nome,
        path: row.path || '',
        primaryColor: row.primaryColor || theme.palette.primary.main,
        secondaryColor: row.secondaryColor || theme.palette.secondary.main,
        textColor: row.textColor || theme.palette.text.primary,
        primaryColorApp: row.primaryColorApp,
        secondaryColorApp: row.secondaryColorApp,
        tertiaryColorApp: row.tertiaryColorApp,
      },
    });
  };

  const renderColorBox = (color, defaultColor) => (
    <Box
      title={color || defaultColor}
      className={classes.colorBox}
      bgcolor={color || defaultColor}
      border="1px solid rgba(0, 0, 0, 0.2)"
    />
  );

  /**
   * Searching shows the first page of the matches, not the page being read.
   * The copy is deliberate: pressing Search re-reads the list even when the
   * text has not changed, so the button always answers with fresh rows.
   */
  const applyFilter = () => {
    setPaginationConfig((prev) => ({ ...prev, loadedPage: 0 }));
    setAppliedFilter({ ...filter });
  };

  const generateFilterColumn = ({ id, placeholder, filterValue, onChange }) => {
    return {
      Header: placeholder,
      id: id,
      accessor: (d) => d[id],
      filterable: true,
      minWidth: 120,
      className: classes.column,
      Filter: (
        <TextField
          variant="outlined"
          fullWidth
          value={filterValue}
          onChange={(e) => {
            const value = e.target.value;
            onChange(value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={`Cerca per ${placeholder}`}
                  onClick={applyFilter}
                >
                  <Search />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      ),
    };
  };

  const columns = [
    generateFilterColumn({
      id: 'id',
      placeholder: 'ID',
      filterValue: filter.brandId,
      onChange: (value) => {
        if (/^\d*$/.test(value)) {
          setFilter((prev) => ({
            ...prev,
            brandId: value,
          }));
        }
      },
    }),
    generateFilterColumn({
      id: 'nome',
      placeholder: 'Brand',
      filterValue: filter.brandName,
      onChange: (value) => {
        setFilter((prev) => ({
          ...prev,
          brandName: value,
        }));
      },
    }),
    {
      Header: 'Colore primario',
      accessor: 'primaryColor',
      filterable: false,
      Cell: ({ value }) => renderColorBox(value, theme.palette.primary.main),
      className: classes.column,
    },
    {
      Header: 'Colore secondario',
      accessor: 'secondaryColor',
      filterable: false,
      Cell: ({ value }) => renderColorBox(value, theme.palette.secondary.main),
      className: classes.column,
    },
    {
      Header: 'Colore testo',
      accessor: 'textColor',
      filterable: false,
      Cell: ({ value }) => renderColorBox(value, theme.palette.text.primary),
      className: classes.column,
    },
    {
      Header: 'Modifica template',
      id: 'modifica',
      filterable: false,
      accessor: (row) => (
        <IconButton
          aria-label={`Modifica template di ${row.nome}`}
          onClick={() => handleClick(row)}
          style={{ color: theme.palette.primary.main }}
        >
          <Settings />
        </IconButton>
      ),
    },
  ];

  const onPageChange = (pageIndex) => {
    setPaginationConfig((prev) => ({ ...prev, loadedPage: pageIndex }));
  };

  return (
    <div className={classes.container}>
      {loading ? (
        <SpinnerComponent size={24} />
      ) : (
        <div className={classes.contentWrapper}>
          <TablePageToolbar
            actions={
              <NewRecordButton label="Crea nuovo brand" disabled={false} />
            }
          />
          <BasicTable
            data={brands}
            columns={columns}
            filterable={false}
            resizable={true}
            sortable={false}
            defaultPageSize={paginationConfig.pageSize}
            page={paginationConfig.loadedPage}
            pages={paginationConfig.totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default BrandTable;
